import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { useSelector, useDispatch } from "react-redux";
import { toggle } from "@/state/toggle/toggle.slice";
import {
  LayoutDashboard,
  Package,
  ShoppingCart, DollarSign,
  Users,
  Truck,
  Box,
  Settings,
  BarChart3,
  Tag,
  Shield,
  Store,
  KeyRound,
  Ruler,
  Activity,
  Receipt,
  ListOrdered,
  PlusCircle,
  Building2,
  Undo2,
  ChartLine,
  TrendingUp,
  ArrowLeftRight,
  Search,
  X,
  Trash2,
} from "lucide-react";

// Navigation items organized by groups
const navGroups = [
  {
    name: "Quick Access",
    items: [
      { name: "New Sale", icon: PlusCircle, href: "/dashboard/sales/create", permission: "Sales" },
      { name: "Add Product", icon: PlusCircle, href: "/dashboard/products/add-product", permission: "Products" },
      { name: "All Sales", icon: ListOrdered, href: "/dashboard/sales", permission: "Sales" },
      { name: "Products", icon: Box, href: "/dashboard/products", permission: "Products" },
    ]
  },
  {
    name: "Overview",
    items: [
      {
        name: "Dashboard",
        icon: LayoutDashboard,
        href: "/dashboard",
        permission: "Dashboard"
      },
    ]
  },
  {
    name: "Sales & Orders",
    items: [
      {
        name: "Sales",
        icon: DollarSign,
        href: "/dashboard/sales",
        permission: "Sales",
        subItems: [
          { name: "All Sales", icon: ListOrdered, href: "/dashboard/sales" },
          { name: "New Sale", icon: PlusCircle, href: "/dashboard/sales/create" },
          { name: "Drafts", icon: Receipt, href: "/dashboard/sales/drafts" },
          { name: "Surplus Sales", icon: TrendingUp, href: "/dashboard/sales/surplus", permission: "ManageSurplusSales" },
          { name: "Returns", icon: Undo2, href: "/dashboard/sales/returns", permission: ["Returns", "Sales"] },
        ],
      },
      {
        name: "Customers",
        icon: Users,
        href: "/dashboard/customers",
        permission: ["Customers", "Sales"]
      },
    ]
  },
  {
    name: "Inventory",
    items: [
      {
        name: "Products",
        icon: Box,
        href: "/dashboard/products",
        permission: "Products",
        subItems: [
          { name: "Products List", icon: ListOrdered, href: "/dashboard/products" },
          { name: "Add Product", icon: PlusCircle, href: "/dashboard/products/add-product" },
        ],
      },
      {
        name: "Inventory",
        icon: Package,
        href: "/dashboard/inventory",
        permission: ["Stock", "Products"],
        subItems: [
          { name: "Stock Overview", icon: ListOrdered, href: "/dashboard/inventory" },
          { name: "Transfers", icon: ArrowLeftRight, href: "/dashboard/inventory/transfers" },
        ],
      },
      {
        name: "Purchases",
        icon: ShoppingCart,
        href: "/dashboard/purchases",
        permission: "Purchases",
        subItems: [
          { name: "All Purchases", icon: ListOrdered, href: "/dashboard/purchases" },
          { name: "Add Purchase", icon: PlusCircle, href: "/dashboard/purchases/add" },
          { name: "Purchase Returns", icon: Undo2, href: "/dashboard/purchases/returns-all/returns" },
        ],
      },
      {
        name: "Suppliers",
        icon: Truck,
        href: "/dashboard/suppliers",
        permission: "Sales"
      },
    ]
  },
  {
    name: "Reports & Analytics",
    items: [
      {
        name: "Reports",
        icon: BarChart3,
        href: "/dashboard/reports/sales",
        // Visible with "Reports", or with any sub-item permission the role holds
        // (e.g. a role given only "Expenses" still gets the Expenses report).
        permission: ["Reports", "Expenses"],
        subItems: [
          { name: "Sales Report", icon: ChartLine, href: "/dashboard/reports/sales", permission: "Reports" },
          { name: "Activity Logs", icon: Activity, href: "/dashboard/reports/activities", permission: "Reports" },
          { name: "Expenses", icon: Receipt, href: "/dashboard/reports/expenses", permission: ["Expenses", "Reports"] },
          { name: "Products Analytics", icon: BarChart3, href: "/dashboard/reports/top-products", permission: "Reports" },
          { name: "Inventory Valuation", icon: Package, href: "/dashboard/reports/inventory-valuation", permission: "Reports" },
          { name: "Expiring Stock", icon: Receipt, href: "/dashboard/reports/expiring-stock", permission: "Reports" },
        ],
      },
    ]
  },
  {
    name: "Configuration",
    items: [
      {
        name: "Settings",
        icon: Settings,
        href: "/dashboard/settings/business",
        permission: "Settings",
        subItems: [
          { name: "Business", icon: Building2, href: "/dashboard/settings/business" },
          { name: "Shops", icon: Store, href: "/dashboard/settings/shops" },
          { name: "Categories", icon: Tag, href: "/dashboard/settings/categories" },
          { name: "Brands", icon: Tag, href: "/dashboard/settings/brands" },
          { name: "Units", icon: Ruler, href: "/dashboard/settings/units" },
          { name: "Warranties", icon: Shield, href: "/dashboard/settings/warranties" },
          { name: "Discounts", icon: Receipt, href: "/dashboard/settings/discounts" },
          // Last in the list on purpose — destructive, and nobody should
          // reach it on the way to something else.
          { name: "Clear Data", icon: Trash2, href: "/dashboard/settings/data" },
        ],
      },
      {
        name: "Users & Roles",
        icon: Shield,
        href: "/dashboard/settings/users",
        permission: ["Users", "Settings"],
        subItems: [
          { name: "Users", icon: Users, href: "/dashboard/settings/users", permission: ["Users", "Settings"] },
          { name: "Roles", icon: KeyRound, href: "/dashboard/settings/roles", permission: ["Users", "Settings"] },
        ],
      },
    ]
  },
];

export default function SleekSidebar() {
  const [searchQuery, setSearchQuery] = useState("");
  const { pathname } = useLocation();

  const { user } = useSelector((state) => state.auth);
  const { isOpen } = useSelector((state) => state.toggle);
  const dispatch = useDispatch();

  // Filter navigation groups and items based on permissions
  const permittedGroups = useMemo(() => {
    if (!user || !user.role || !user.role.permissions) return [];

    let userPerms = [];
    try {
      userPerms = typeof user.role.permissions === 'string'
        ? JSON.parse(user.role.permissions)
        : user.role.permissions;
    } catch (e) {
      return [];
    }

    // A `permission` may be a single string or an array (any-of), so a role can
    // be granted access via either a specific permission or a broader one.
    const allows = (perm) => {
      if (!perm) return true;
      const list = Array.isArray(perm) ? perm : [perm];
      return list.some(p => userPerms.includes(p));
    };

    return navGroups.map(group => ({
      ...group,
      items: group.items
        .filter(item => {
          if (allows(item.permission)) return true;
          // Parent gate failed, but show it if the role holds an explicit
          // sub-item permission (e.g. only "Expenses" under Reports).
          return (item.subItems || []).some(s => s.permission && allows(s.permission));
        })
        .map(item => {
          const parentOk = allows(item.permission);
          return {
            ...item,
            subItems: item.subItems?.filter(subItem =>
              // Sub-items without their own permission inherit the parent's.
              subItem.permission ? allows(subItem.permission) : parentOk
            )
          };
        })
    })).filter(group => group.items.length > 0);
  }, [user]);

  // Apply search filter
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return permittedGroups;
    const query = searchQuery.toLowerCase();

    return permittedGroups.map(group => {
      const filteredItems = [];

      group.items.forEach(item => {
        const itemMatches = item.name.toLowerCase().includes(query);
        const subItemsMatches = item.subItems?.filter(sub =>
          sub.name.toLowerCase().includes(query)
        ) || [];

        if (itemMatches) {
          filteredItems.push({ ...item, subItems: subItemsMatches.length > 0 ? subItemsMatches : item.subItems });
        } else if (subItemsMatches.length > 0) {
          filteredItems.push({ ...item, subItems: subItemsMatches });
        }
      });

      return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);
  }, [searchQuery, permittedGroups]);

  // Prevent background scrolling when launcher is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      // Clear search when closed
      setTimeout(() => setSearchQuery(""), 300);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const closeLauncher = () => {
    if (isOpen) {
      dispatch(toggle());
    }
  };

  const AppIcon = ({ item }) => {
    const isActive = pathname === item.href;
    return (
      <Link
        to={item.href}
        onClick={closeLauncher}
        className={cn(
          "flex flex-col items-center justify-start gap-2 p-2 rounded-2xl group transition-all duration-300",
          "hover:bg-white/10 dark:hover:bg-white/5 active:scale-95 text-center col-span-1",
        )}
      >
        <div className={cn(
          "flex items-center justify-center rounded-2xl shadow-sm transition-all duration-300 h-14 w-14 md:h-16 md:w-16",
          isActive
            ? "bg-gradient-to-br from-red-500 to-orange-500 text-white shadow-red-500/30"
            : "bg-white dark:bg-white/5 backdrop-blur-md text-foreground shadow-lg group-hover:shadow-xl group-hover:bg-gray-50 dark:group-hover:bg-white/10"
        )}>
          <item.icon className="h-6 w-6 md:h-8 md:w-8" strokeWidth={isActive ? 2 : 1.5} />
        </div>
        <span className={cn(
          "text-xs md:text-sm font-medium tracking-tight line-clamp-2",
          isActive ? "text-foreground font-semibold" : "text-foreground/80 group-hover:text-foreground"
        )}>
          {item.name}
        </span>
      </Link>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, backdropFilter: "blur(0px)", scale: 1.05 }}
          animate={{ opacity: 1, backdropFilter: "blur(20px)", scale: 1 }}
          exit={{ opacity: 0, backdropFilter: "blur(0px)", scale: 0.95 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} // smooth ease out curve
          className="fixed inset-0 z-[100] flex flex-col bg-slate-100/40 dark:bg-[#1a1519]/50 overflow-y-auto"
        >
          {/* Header Area */}
          <div className="sticky top-0 z-10 p-6 md:p-10 flex flex-col items-center justify-center gap-8 bg-gradient-to-b from-slate-100/60 dark:from-[#1a1519]/60 to-transparent pb-10">
            {/* Close Button placed absolutely */}
            <button
              onClick={closeLauncher}
              className="absolute top-6 right-6 p-4 rounded-full bg-white/50 hover:bg-white dark:bg-white/10 dark:hover:bg-white/20 transition-colors text-foreground shadow-sm hover:shadow-md"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="w-full max-w-2xl mt-8">
              <div className="relative group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-red-500 transition-colors" />
                <Input
                  autoFocus
                  placeholder="App Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-16 h-12 md:h-16 text-xl md:text-xl rounded-full bg-white dark:bg-black/20 border-white/20 dark:border-white/10 shadow-lg text-foreground focus-visible:ring-red-500/50 backdrop-blur-md"
                />
              </div>
            </div>
          </div>

          {/* Apps Grid */}
          <div className="flex-1 w-full max-w-7xl mx-auto px-6 pb-20 pt-4">
            {filteredGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground delay-100 animate-in fade-in">
                <Search className="h-16 w-16 mb-4 opacity-20" />
                <p className="text-xl">No applications found matching "{searchQuery}"</p>
              </div>
            ) : (
              <div className="flex flex-col gap-10">
                {filteredGroups.map((group, idx) => (
                  <motion.div
                    key={group.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + idx * 0.05, duration: 0.4, ease: "easeOut" }}
                  >
                    <h3 className="text-base md:text-xl font-semibold mb-4 md:mb-6 px-4 text-foreground/90 border-b border-border/50 pb-2">
                      {group.name}
                    </h3>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-x-2 gap-y-6 md:gap-y-8">
                      {group.items.map((item) => {
                        // Filter out sub-items that point to the exact same href as the parent to avoid duplicates
                        const uniqueSubItems = item.subItems?.filter(sub => sub.href !== item.href);
                        return (
                          <div key={item.name} className="contents">
                            {/* Main Item */}
                            <AppIcon item={item} />

                            {/* Sub Items */}
                            {uniqueSubItems?.map((subItem) => (
                              <AppIcon key={subItem.name} item={subItem} />
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
